import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { 
  getTargetCloseFriends, 
  getEmotionalDepthPreference, 
  isQuantityRealistic,
  calculateNeedsProfile as calculateNeedsProfileUtil,
  generateFriendshipGoalsFromProfile 
} from '../utils/friendshipAssessment';
import { calculateProgress } from '../utils/friendshipProgress';

const FriendshipNeedsAssessment = ({ onBack }) => {
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState('intro'); // 'intro', 'assessment', 'results', 'goals'
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [needsProfile, setNeedsProfile] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingGoalId, setEditingGoalId] = useState(null);
  const [editingGoalText, setEditingGoalText] = useState('');
  const [friends, setFriends] = useState([]);


  const getGoalDisplayText = (goal) => {
    if (!needsProfile || !needsProfile.preferences) return goal.text;

    const prefs = needsProfile.preferences;

    // Handle legacy auto-goals that may have saved the translation key as text
    if (
      goal.id === 'goal-close-friends-count' ||
      goal.text === 'autoGoalCloseFriendsCount'
    ) {
      const target = prefs.closeFriendCount?.target;
      if (!target) return goal.text;
      return t('autoGoalCloseFriendsCount', { target });
    }

    if (
      goal.id === 'goal-emotional-depth' ||
      goal.text === 'autoGoalEmotionalDepth'
    ) {
      const depthLabel = prefs.emotionalDepth?.label;
      if (!depthLabel) return goal.text;
      return t('autoGoalEmotionalDepth', { depth: depthLabel.toLowerCase() });
    }

    return goal.text;
  };

  const questions = [
    {
      id: 'connection-depth',
      category: t('categoryConnectionDepth'),
      question: t('questionConnectionDepth'),
      options: [
        { value: 'deep', label: t('optionDeep'), score: { emotional: 5, social: 3 } },
        { value: 'balanced', label: t('optionBalanced'), score: { emotional: 3, social: 4 } },
        { value: 'casual', label: t('optionCasual'), score: { emotional: 2, social: 5 } }
      ]
    },
    {
      id: 'social-energy',
      category: t('categorySocialEnergy'),
      question: t('questionSocialEnergy'),
      options: [
        { value: 'high', label: t('optionHigh'), score: { frequency: 5, social: 5 } },
        { value: 'moderate', label: t('optionModerate'), score: { frequency: 3, social: 3 } },
        { value: 'low', label: t('optionLow'), score: { frequency: 2, social: 2 } }
      ]
    },
    {
      id: 'support-type',
      category: t('categorySupportNeeds'),
      question: t('questionSupportType'),
      options: [
        { value: 'emotional', label: t('optionEmotional'), score: { emotional: 5, support: 5 } },
        { value: 'practical', label: t('optionPractical'), score: { practical: 5, social: 4 } },
        { value: 'both', label: t('optionBoth'), score: { emotional: 4, practical: 4, support: 4 } }
      ]
    },
    {
      id: 'communication-style',
      category: t('categoryCommunication'),
      question: t('questionCommunicationStyle'),
      options: [
        { value: 'frequent', label: t('optionFrequent'), score: { frequency: 5, communication: 5 } },
        { value: 'regular', label: t('optionRegular'), score: { frequency: 3, communication: 4 } },
        { value: 'occasional', label: t('optionOccasional'), score: { frequency: 2, communication: 3 } }
      ]
    },
    {
      id: 'friendship-goals',
      category: t('categoryGoals'),
      question: t('questionFriendshipGoals'),
      options: [
        { value: 'growth', label: t('optionGrowth'), score: { growth: 5, emotional: 4 } },
        { value: 'fun', label: t('optionFun'), score: { social: 5, fun: 5 } },
        { value: 'support', label: t('optionSupport'), score: { support: 5, emotional: 4 } },
        { value: 'all', label: t('optionAll'), score: { growth: 3, social: 3, support: 3, emotional: 3 } }
      ]
    },
    {
      id: 'friendship-quantity',
      category: t('categorySocialCircle'),
      question: t('questionFriendshipQuantity'),
      options: [
        { value: 'few', label: t('optionFew'), score: { depth: 5, emotional: 5 } },
        { value: 'moderate', label: t('optionModerateCircle'), score: { depth: 3, social: 4 } },
        { value: 'many', label: t('optionMany'), score: { social: 5, frequency: 4 } }
      ]
    },
    {
      id: 'time-investment',
      category: t('categoryTimeInvestment'),
      question: t('questionTimeInvestment'),
      options: [
        { value: 'high', label: t('optionHighTime'), score: { frequency: 5, time: 5 } },
        { value: 'moderate', label: t('optionModerateTime'), score: { frequency: 3, time: 3 } },
        { value: 'limited', label: t('optionLimited'), score: { depth: 4, time: 2 } }
      ]
    },
    {
      id: 'conflict-handling',
      category: t('categoryConflictBoundaries'),
      question: t('questionConflictHandling'),
      options: [
        { value: 'direct', label: t('optionDirect'), score: { communication: 5, emotional: 4, repair: 5 } },
        { value: 'gentle', label: t('optionGentle'), score: { emotional: 4, communication: 3, repair: 4 } },
        { value: 'avoid', label: t('optionAvoid'), score: { social: 3, emotional: 2, repair: 2 } }
      ]
    },
    {
      id: 'repair-style',
      category: t('categoryRepairStyle'),
      question: t('questionRepairStyle'),
      options: [
        { value: 'talk-through', label: t('optionTalkThrough'), score: { communication: 5, emotional: 4, repair: 5 } },
        { value: 'apology-behavior', label: t('optionApologyBehavior'), score: { emotional: 5, support: 4, repair: 5 } },
        { value: 'time-normal', label: t('optionTimeNormal'), score: { social: 4, emotional: 2, repair: 3 } },
        { value: 'shared-experiences', label: t('optionSharedExperiences'), score: { social: 5, fun: 4, repair: 4 } }
      ]
    },
    {
      id: 'initiation-balance',
      category: t('categoryInitiationBalance'),
      question: t('questionInitiationBalance'),
      options: [
        { value: 'mostly-me', label: t('optionMostlyMe'), score: { frequency: 5, communication: 4 } },
        { value: 'about-equal', label: t('optionAboutEqual'), score: { frequency: 4, communication: 4 } },
        { value: 'mostly-them', label: t('optionMostlyThem'), score: { frequency: 3, communication: 3 } },
        { value: 'varies', label: t('optionVaries'), score: { frequency: 3, social: 4 } }
      ]
    }
  ];

  useEffect(() => {
    loadAssessmentData();
  }, [currentUser]);

  useEffect(() => {
    if (currentStep === 'goals' && needsProfile && currentUser) {
      loadFriends();
    }
  }, [currentStep, needsProfile, currentUser]);

  const loadAssessmentData = async () => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (userData.friendshipNeeds) {
          setNeedsProfile(userData.friendshipNeeds);
          setAnswers(userData.friendshipNeeds.answers || {});
          setGoals(userData.friendshipNeeds.goals || []);
          // Calculate recommendations from saved profile
          const savedRecommendations = calculateRecommendations(userData.friendshipNeeds);
          setRecommendations(savedRecommendations);
          setCurrentStep('results');
        }
      }
    } catch (error) {
      console.error('Error loading assessment data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, option) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: option
    }));
  };

  const calculateNeedsProfile = () => {
    return calculateNeedsProfileUtil(questions, answers, t);
  };

  const handleCompleteAssessment = () => {
    const profile = calculateNeedsProfile();
    const recs = calculateRecommendations(profile);

    // Generate automatic goals based on preferences (emotional depth, quantity)
    const autoGoals = generateFriendshipGoalsFromProfile(profile, t);
    const mergedGoals = [...goals, ...autoGoals].filter(
      (goal, index, self) =>
        self.findIndex(g => g.id === goal.id) === index
    );

    setNeedsProfile(profile);
    setRecommendations(recs);
    setGoals(mergedGoals);
    setCurrentStep('results');
    saveAssessmentData(profile, mergedGoals);
  };

  const saveAssessmentData = async (profile = null, goalsOverride = null) => {
    if (!currentUser) return;

    setSaving(true);
    try {
      const dataToSave = profile || needsProfile;
      const goalsToPersist = goalsOverride || goals;
      const userRef = doc(db, 'users', currentUser.uid);
      
      await updateDoc(userRef, {
        friendshipNeeds: {
          ...dataToSave,
          goals: goalsToPersist
        }
      });
    } catch (error) {
      console.error('Error saving assessment data:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleAddGoal = (goalText) => {
    const newGoal = {
      id: Date.now().toString(),
      text: goalText,
      completed: false,
      createdAt: new Date().toISOString()
    };
    const updatedGoals = [...goals, newGoal];
    setGoals(updatedGoals);
    saveGoals(updatedGoals);
  };

  const startEditingGoal = (goal) => {
    setEditingGoalId(goal.id);
    setEditingGoalText(goal.text || '');
  };

  const cancelEditingGoal = () => {
    setEditingGoalId(null);
    setEditingGoalText('');
  };

  const handleUpdateGoalText = (goalId, newText) => {
    const trimmed = newText.trim();
    if (!trimmed) {
      cancelEditingGoal();
      return;
    }

    const updatedGoals = goals.map(goal =>
      goal.id === goalId ? { ...goal, text: trimmed } : goal
    );
    setGoals(updatedGoals);
    saveGoals(updatedGoals);
    cancelEditingGoal();
  };

  const handleToggleGoal = (goalId) => {
    const updatedGoals = goals.map(goal =>
      goal.id === goalId ? { ...goal, completed: !goal.completed } : goal
    );
    setGoals(updatedGoals);
    saveGoals(updatedGoals);
  };

  const handleDeleteGoal = (goalId) => {
    const updatedGoals = goals.filter(goal => goal.id !== goalId);
    setGoals(updatedGoals);
    saveGoals(updatedGoals);
  };

  const saveGoals = async (goalsToSave) => {
    if (!currentUser) return;

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        'friendshipNeeds.goals': goalsToSave
      });
    } catch (error) {
      console.error('Error saving goals:', error);
    }
  };

  const getNeedName = (need) => {
    const names = {
      emotional: t('needEmotional'),
      social: t('needSocial'),
      frequency: t('needFrequency'),
      support: t('needSupport'),
      practical: t('needPractical'),
      communication: t('needCommunication'),
      growth: t('needGrowth'),
      fun: t('needFun'),
      depth: t('needDepth'),
      time: t('needTime'),
      repair: t('needRepair')
    };
    return names[need] || need.charAt(0).toUpperCase() + need.slice(1);
  };

  const getNeedDescription = (need) => {
    const descriptions = {
      emotional: t('needDescriptionEmotional'),
      social: t('needDescriptionSocial'),
      frequency: t('needDescriptionFrequency'),
      support: t('needDescriptionSupport'),
      practical: t('needDescriptionPractical'),
      communication: t('needDescriptionCommunication'),
      growth: t('needDescriptionGrowth'),
      fun: t('needDescriptionFun'),
      depth: t('needDescriptionDepth'),
      time: t('needDescriptionTime'),
      repair: t('needDescriptionRepair')
    };
    return descriptions[need] || t('needDescriptionDefault');
  };

  const calculateRecommendations = (profile) => {
    if (!profile) return [];

    const recs = [];
    const primaryNeeds = profile.primaryNeeds || [];

    primaryNeeds.forEach(({ need, score }) => {
      if (need === 'emotional' && score >= 4) {
        recs.push(t('recommendationEmotional'));
      }
      if (need === 'frequency' && score >= 4) {
        recs.push(t('recommendationFrequency'));
      }
      if (need === 'social' && score >= 4) {
        recs.push(t('recommendationSocial'));
      }
      if (need === 'support' && score >= 4) {
        recs.push(t('recommendationSupport'));
      }
      if (need === 'depth' && score >= 4) {
        recs.push(t('recommendationDepth'));
      }
      if (need === 'repair' && score >= 4) {
        recs.push(t('recommendationRepair'));
      }
    });

    // Always add a recommendation based on friendship quantity preference
    const answers = profile.answers || {};
    const friendshipQuantityAnswer = answers['friendship-quantity'];
    if (friendshipQuantityAnswer) {
      const quantityValue = friendshipQuantityAnswer.value;
      if (quantityValue === 'few') {
        recs.push(t('recommendationQuantityFew'));
      } else if (quantityValue === 'moderate') {
        recs.push(t('recommendationQuantityModerate'));
      } else if (quantityValue === 'many') {
        recs.push(t('recommendationQuantityMany'));
      }
    }

    return recs.length > 0 ? recs : [
      t('recommendationDefault1'),
      t('recommendationDefault2')
    ];
  };

  const getRecommendations = () => {
    if (recommendations.length > 0) return recommendations;
    if (!needsProfile) return [];
    return calculateRecommendations(needsProfile);
  };

  const loadFriends = async () => {
    if (!currentUser) return;

    try {
      const friendsQuery = query(
        collection(db, 'friends'),
        where('userId', '==', currentUser.uid)
      );
      const friendsSnapshot = await getDocs(friendsQuery);
      const friendsData = friendsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setFriends(friendsData);
    } catch (error) {
      console.error('Error loading friends:', error);
    }
  };


  if (loading) {
    return (
      <div className="friendship-needs-assessment">
        <div className="assessment-loading">
          <div className="loading-spinner"></div>
          <p>Loading your assessment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="friendship-needs-assessment">
      <div className="assessment-header">
        <button className="back-button" onClick={onBack}>
          ← Back
        </button>
        <h1>🎯 {t('friendshipGoals')}</h1>
        <p>{t('answersHelpSetGoals')}</p>
      </div>

      {currentStep === 'intro' && (
        <div className="assessment-intro">
          <div className="intro-content">
            <div className="intro-icon">✨</div>
            <h2>{t('discoverYourFriendshipNeeds')}</h2>
            <p>
              {t('assessmentIntro')}
            </p>
            <ul className="intro-benefits">
              <li>{t('idealFriendshipStyle')}</li>
              <li>{t('socialInteractionEnergy')}</li>
              <li>{t('supportValue')}</li>
              <li>{t('communicationPreferences')}</li>
              <li>{t('realisticGoals')}</li>
            </ul>
            <p className="intro-note">
              {t('answersHelpSetGoals')}
            </p>
            <button 
              className="btn btn-primary btn-large"
              onClick={() => {
                setCurrentStep('assessment');
                setCurrentQuestionIndex(0);
                setAnswers({});
              }}
            >
              {t('startAssessment')}
            </button>
          </div>
        </div>
      )}

      {currentStep === 'assessment' && (
        <div className="assessment-questions">
          <div className="progress-bar">
            <div 
              className="progress-fill"
              style={{ 
                width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` 
              }}
            />
          </div>
          <div className="progress-text">
            {t('question')} {currentQuestionIndex + 1} {t('of')} {questions.length}
          </div>

          {questions.map((question, index) => {
            if (index !== currentQuestionIndex) return null;

            const isLastQuestion = index === questions.length - 1;
            const hasAnswer = answers[question.id];

            return (
              <div key={question.id} className="question-card">
                <div className="question-category">{question.category}</div>
                <h3 className="question-text">{question.question}</h3>
                <div className="question-options">
                  {question.options.map((option) => (
                    <button
                      key={option.value}
                      className={`option-button ${answers[question.id]?.value === option.value ? 'selected' : ''}`}
                      onClick={() => {
                        handleAnswer(question.id, option);
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                <div className="question-actions">
                  {index > 0 && (
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        setCurrentQuestionIndex(index - 1);
                      }}
                    >
                      {t('previous')}
                    </button>
                  )}
                  {hasAnswer && !isLastQuestion && (
                    <button
                      className="btn btn-primary"
                      onClick={() => {
                        setCurrentQuestionIndex(index + 1);
                      }}
                    >
                      {t('next')} →
                    </button>
                  )}
                  {hasAnswer && isLastQuestion && (
                    <button
                      className="btn btn-primary"
                      onClick={handleCompleteAssessment}
                    >
                      {t('finish')}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {currentStep === 'results' && needsProfile && (
        <div className="assessment-results">
          <div className="results-header">
            <h2>{t('yourFriendshipProfile')}</h2>
            <p>{t('basedOnYourAnswers')}</p>
          </div>

          {/* Explicit summary of key targets from the assessment */}
          {needsProfile.preferences && (
            <div className="targets-summary">
              <h3>{t('yourKeyTargets')}</h3>
              <ul>
                {needsProfile.preferences.emotionalDepth && (
                  <li>
                    {t('summaryEmotionalDepth', {
                      depth: needsProfile.preferences.emotionalDepth.label
                    })}
                  </li>
                )}
                {needsProfile.preferences.closeFriendCount && (
                  <li>
                    {t('summaryCloseFriendsQuantity', {
                      target: needsProfile.preferences.closeFriendCount.target
                    })}
                    {!needsProfile.quantityIsRealistic && (
                      <span className="targets-note">
                        {' '}
                        {t('summaryQuantityRealismNote')}
                      </span>
                    )}
                  </li>
                )}
              </ul>
            </div>
          )}

          <div className="primary-needs">
            <h3>{t('yourTopPriorities')}</h3>
            <div className="needs-list">
              {needsProfile.primaryNeeds.map((need, index) => (
                <div key={need.need} className="need-item">
                  <div className="need-rank">#{index + 1}</div>
                  <div className="need-content">
                    <div className="need-name">{getNeedName(need.need)}</div>
                    <div className="need-description">{getNeedDescription(need.need)}</div>
                    <div className="need-score-bar">
                      <div 
                        className="need-score-fill"
                        style={{ width: `${(need.score / 5) * 100}%` }}
                      />
                    </div>
                    <div className="need-score-value">{need.score.toFixed(1)}/5.0</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="recommendations-section">
            <h3>{t('recommendations')}</h3>
            <ul className="recommendations-list">
              {getRecommendations().map((rec, index) => (
                <li key={index}>{rec}</li>
              ))}
            </ul>
          </div>

          <div className="results-actions">
            <button
              className="btn btn-secondary"
              onClick={() => {
                setAnswers({});
                setCurrentQuestionIndex(0);
                setCurrentStep('assessment');
              }}
            >
              {t('retakeAssessment')}
            </button>
            <button
              className="btn btn-primary"
              onClick={() => setCurrentStep('goals')}
            >
              {t('viewGoals')} →
            </button>
          </div>
        </div>
      )}

      {currentStep === 'goals' && (
        <div className="assessment-goals">
          <div className="goals-header">
            <h2>{t('yourFriendshipGoals')}</h2>
            <p>{t('setYourGoals')}</p>
          </div>

          {/* Progress Tracking Section */}
          {(() => {
            const progress = calculateProgress({ friends, friendshipNeeds: needsProfile });
            if (!progress || (!progress.closeFriendsCount && !progress.emotionalDepth)) return null;

            return (
              <div className="goals-progress-tracking">
                <h3>{t('progressTowardGoals')}</h3>
                <div className="progress-cards">
                  {progress.closeFriendsCount && (
                    <div className="progress-card">
                      <div className="progress-card-header">
                        <h4>{t('closeFriendsProgress')}</h4>
                        <div className="progress-card-stats">
                          <span className="progress-current">{progress.closeFriendsCount.current}</span>
                          <span className="progress-separator">/</span>
                          <span className="progress-target">{progress.closeFriendsCount.target}</span>
                        </div>
                      </div>
                      <div className="progress-bar-container">
                        <div 
                          className="progress-bar-fill"
                          style={{ width: `${progress.closeFriendsCount.percentage}%` }}
                        />
                      </div>
                      {progress.closeFriendsCount.remaining > 0 ? (
                        <p className="progress-message">
                          {t('closeFriendsRemaining', { count: progress.closeFriendsCount.remaining })}
                        </p>
                      ) : (
                        <p className="progress-message success">
                          {t('closeFriendsGoalReached')}
                        </p>
                      )}
                    </div>
                  )}

                  {progress.emotionalDepth && (
                    <div className="progress-card">
                      <div className="progress-card-header">
                        <h4>{t('emotionalDepthProgress')}</h4>
                      </div>
                      <p className="progress-message">
                        {progress.emotionalDepth.hasCloseFriends 
                          ? t('emotionalDepthHasFriends', { 
                              count: progress.emotionalDepth.totalCloseFriends,
                              level: needsProfile.preferences.emotionalDepth.label.toLowerCase()
                            })
                          : t('emotionalDepthNoFriends')
                        }
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {needsProfile?.preferences && (
            <div className="goals-key-targets">
              <h3>{t('goalsAnchoredInTargets')}</h3>
              <p>{t('goalsAnchoredInTargetsHelp')}</p>
              <ul>
                {needsProfile.preferences.emotionalDepth && (
                  <li>
                    {t('summaryEmotionalDepth', {
                      depth: needsProfile.preferences.emotionalDepth.label
                    })}
                  </li>
                )}
                {needsProfile.preferences.closeFriendCount && (
                  <li>
                    {t('summaryCloseFriendsQuantity', {
                      target: needsProfile.preferences.closeFriendCount.target
                    })}
                  </li>
                )}
              </ul>
            </div>
          )}

          {/* Recommendations Section */}
          {recommendations.length > 0 && (
            <div className="recommendations-for-goals">
              <h3>{t('suggestedGoalsBasedOnAssessment')}</h3>
              <p className="recommendations-subtitle">{t('clickRecommendationToAddGoal')}</p>
              <div className="recommendations-chips">
                {recommendations.map((rec, index) => {
                  // Check if this recommendation is already a goal
                  const isAlreadyGoal = goals.some(goal => 
                    goal.text.toLowerCase() === rec.toLowerCase()
                  );
                  
                  return (
                    <button
                      key={index}
                      className={`recommendation-chip ${isAlreadyGoal ? 'added' : ''}`}
                      onClick={() => {
                        if (!isAlreadyGoal) {
                          handleAddGoal(rec);
                        }
                      }}
                      disabled={isAlreadyGoal}
                    >
                      {isAlreadyGoal ? '✓ ' : '+ '}
                      {rec}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="goal-input-section">
            <input
              type="text"
              className="goal-input"
              placeholder={t('goalInputPlaceholder')}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && e.target.value.trim()) {
                  handleAddGoal(e.target.value.trim());
                  e.target.value = '';
                }
              }}
            />
            <button
              className="btn btn-primary"
              onClick={(e) => {
                const input = e.target.previousElementSibling;
                if (input.value.trim()) {
                  handleAddGoal(input.value.trim());
                  input.value = '';
                }
              }}
            >
              {t('addGoal')}
            </button>
          </div>

          <div className="goals-list">
            {goals.length === 0 ? (
              <div className="empty-goals">
                <p>{t('noGoalsYet')}</p>
              </div>
            ) : (
              goals.map((goal) => {
                const isAutoGoal =
                  goal.id === 'goal-close-friends-count' ||
                  goal.id === 'goal-emotional-depth';

                const isEditing = editingGoalId === goal.id;

                return (
                  <div key={goal.id} className={`goal-item ${goal.completed ? 'completed' : ''}`}>
                    <input
                      type="checkbox"
                      checked={goal.completed}
                      onChange={() => handleToggleGoal(goal.id)}
                      className="goal-checkbox"
                    />

                    {isEditing ? (
                      <div className="goal-edit-wrapper">
                        <input
                          type="text"
                          className="goal-edit-input"
                          value={editingGoalText}
                          onChange={(e) => setEditingGoalText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleUpdateGoalText(goal.id, editingGoalText);
                            }
                            if (e.key === 'Escape') {
                              cancelEditingGoal();
                            }
                          }}
                          autoFocus
                        />
                        <button
                          className="goal-edit-save btn btn-secondary"
                          onClick={() => handleUpdateGoalText(goal.id, editingGoalText)}
                        >
                          {t('saveGoals')}
                        </button>
                        <button
                          className="goal-edit-cancel btn btn-text"
                          onClick={cancelEditingGoal}
                        >
                          {t('skipForNow')}
                        </button>
                      </div>
                    ) : (
                      <>
                        <span className="goal-text">{getGoalDisplayText(goal)}</span>
                        {!isAutoGoal && (
                          <button
                            className="goal-edit"
                            onClick={() => startEditingGoal(goal)}
                            title={t('editProfile')}
                          >
                            ✏️
                          </button>
                        )}
                      </>
                    )}

                    <button
                      className="goal-delete"
                      onClick={() => handleDeleteGoal(goal.id)}
                    >
                      ×
                    </button>
                  </div>
                );
              })
            )}
          </div>

          <div className="goals-progress">
            <h3>{t('progressOverview')}</h3>
            <div className="progress-stats">
              <div className="progress-stat">
                <div className="stat-number">{goals.filter(g => g.completed).length}</div>
                <div className="stat-label">{t('completed')}</div>
              </div>
              <div className="progress-stat">
                <div className="stat-number">{goals.length}</div>
                <div className="stat-label">{t('totalGoals')}</div>
              </div>
              <div className="progress-stat">
                <div className="stat-number">
                  {goals.length > 0 
                    ? Math.round((goals.filter(g => g.completed).length / goals.length) * 100)
                    : 0}%
                </div>
                <div className="stat-label">{t('progress')}</div>
              </div>
            </div>
            {goals.length > 0 && (
              <div className="progress-bar-large">
                <div 
                  className="progress-fill-large"
                  style={{ 
                    width: `${(goals.filter(g => g.completed).length / goals.length) * 100}%` 
                  }}
                />
              </div>
            )}
          </div>

          <div className="goals-actions">
            <button
              className="btn btn-secondary"
              onClick={() => setCurrentStep('results')}
            >
              ← {t('back')} {t('to')} {t('overview')}
            </button>
            {onBack && (
              <button
                className="btn btn-primary"
                onClick={onBack}
              >
                {t('finish')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FriendshipNeedsAssessment;

